/* HM Custom Cart Drawer
 * Web component definitions for the HM cart drawer.
 *
 * Components defined here:
 *   <hm-cart-line-item>     — Cart line wrapper (Phase 4)
 *   <hm-cart-stepper>       — Qty +/- + delete (Phase 4)
 *   <hm-cart-threshold>     — Tier progress bar (Phase 3)
 *   <hm-cart-gift-wrap>     — Per-line gift packaging upsell (Phase 5)
 *   <hm-cart-recommendations> — Upsell carousel (Phase 6)
 *   <hm-cart-terms-gate>    — Terms checkbox → checkout button gate (Phase 7)
 *
 * All cart-mutating components dispatch `cart:change` after `/cart/*.js`
 * mutations. Impact's native <cart-drawer> listens to `cart:change` and
 * re-fetches /?sections=cart-drawer, swapping the rendered HTML in place
 * (which destroys + recreates our HM elements with fresh data attributes).
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
   * Shared cart-mutation helper. All cart fetches funnel through here
   * so we have one place to change auth headers, error handling, etc.
   *
   * Pass `dispatch: false` to suppress the cart:change event — needed
   * for the gift-wrap swap (sequential remove + add) so Impact's
   * <cart-drawer> doesn't re-fetch the section mid-swap and flash
   * an empty gift-wrap line between the two requests.
   * ───────────────────────────────────────────────────────────── */

  async function postCartFetch(endpoint, body, { dispatch = true } = {}) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    // Impact's <cart-drawer> handler (theme.js) reads
    // `event.detail.cart.sections[sectionId]` with no fallback — if that key
    // is missing, the drawer throws and never re-renders. The canonical
    // Impact pattern is to:
    //   1. Dispatch cart:prepare-bundled-sections so listeners (cart-drawer,
    //      Klaviyo, etc.) push their section ids into the array.
    //   2. Include `sections=<csv>` in the POST body so Shopify returns
    //      pre-rendered section HTML alongside the cart JSON.
    //   3. Dispatch cart:change with the full response — `sections` is now
    //      part of `json` because step 2 asked Shopify to include it.
    //
    // This was previously masked by ReBuy, which silently bundled sections.
    // After hm-disable-rebuy.liquid killed ReBuy, the gap surfaced.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const payload = sectionsToBundle.length
      ? Object.assign({}, body, { sections: sectionsToBundle.join(',') })
      : body;

    const res = await fetch(root + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(endpoint + ' ' + res.status);
    const json = await res.json();
    if (dispatch) {
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart: json } }));
    }
    return json;
  }

  /* ─────────────────────────────────────────────────────────────
   * Shared screen-reader live region.
   *
   * Impact's <cart-drawer> swaps drawer HTML on every cart:change, which
   * destroys any aria-live element inside the drawer (so the per-element
   * `aria-live` attributes on stepper-value / total / threshold-message
   * never actually announce — the element is replaced, not mutated). To
   * fix this we keep ONE polite live region appended to <body> that
   * persists across renders and is mutated by JS after each fetch.
   *
   * Visually invisible via inline styles — no CSS dependency added.
   * Created lazily on first announce so the SR doesn't see a stray empty
   * region on pages where the cart drawer never opens.
   * ───────────────────────────────────────────────────────────── */

  let _liveRegion = null;
  function getLiveRegion() {
    if (_liveRegion) return _liveRegion;
    _liveRegion = document.createElement('div');
    _liveRegion.setAttribute('role', 'status');
    _liveRegion.setAttribute('aria-live', 'polite');
    _liveRegion.setAttribute('aria-atomic', 'true');
    _liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    document.body.appendChild(_liveRegion);
    return _liveRegion;
  }
  function announce(text) {
    if (!text) return;
    const region = getLiveRegion();
    // Clearing then setting forces SR to re-announce even if text is identical.
    region.textContent = '';
    // Small timeout avoids the SR coalescing the clear + set into nothing.
    setTimeout(() => { region.textContent = text; }, 50);
  }

  /* Shared module-scoped DOMParser — created once instead of per-fetch. */
  const _domParser = new DOMParser();

  /* Detect prefers-reduced-motion once. The match result can flip during a
   * session but the drawer's animation cost is so low that re-evaluating
   * per animation isn't worth the overhead. */
  const _reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-stepper> — qty +/− buttons inside a line item.
   *
   * Reads the parent <hm-cart-line-item>'s data-line-key,
   * data-current-qty, and data-max-qty to compute and validate
   * the new quantity. Sets data-busy on the line item during the
   * fetch so its delete + stepper buttons disable for that window.
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-stepper')) {
    class HmCartStepper extends HTMLElement {
      #abortController;

      connectedCallback() {
        this.#abortController = new AbortController();
        this.addEventListener('click', this.#onClick.bind(this), { signal: this.#abortController.signal });
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      async #onClick(event) {
        const button = event.target.closest('[data-step]');
        if (!button) return;

        const lineItem = this.closest('hm-cart-line-item');
        if (!lineItem) return;
        if (lineItem.dataset.busy === 'true') return;

        const step = parseInt(button.dataset.step, 10);

        // Lock first, THEN re-read the quantity. A concurrent cart:change
        // re-render could replace this element between the user's click and
        // here; if we read current first, a stale-1 read followed by a
        // decrement sends qty=0 (silent delete). Setting busy first
        // serialises against further clicks on this element.
        lineItem.dataset.busy = 'true';

        const current = parseInt(lineItem.dataset.currentQty, 10) || 0;
        const newQty = current + step;
        if (newQty < 1) {
          delete lineItem.dataset.busy;
          return;
        }
        const max = parseInt(lineItem.dataset.maxQty, 10);
        if (!isNaN(max) && newQty > max) {
          delete lineItem.dataset.busy;
          return;
        }

        try {
          await postCartFetch('cart/change.js', { id: lineItem.dataset.lineKey, quantity: newQty });
          announce('Quantity updated');
          // Drawer section refresh will replace this element; no need to clear busy
        } catch (err) {
          console.error('[hm-cart-stepper]', err);
          delete lineItem.dataset.busy;
        }
      }
    }
    customElements.define('hm-cart-stepper', HmCartStepper);
  }

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-line-item> — wraps a single cart line.
   *
   * Handles the trash-icon click (delete = qty 0). Quantity stepper
   * clicks are handled by <hm-cart-stepper>; we only respond to
   * [data-delete-line] here so the two components don't double-fire
   * on bubbling.
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-line-item')) {
    class HmCartLineItem extends HTMLElement {
      #abortController;

      connectedCallback() {
        this.#abortController = new AbortController();
        this.addEventListener('click', this.#onClick.bind(this), { signal: this.#abortController.signal });
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      async #onClick(event) {
        const button = event.target.closest('[data-delete-line]');
        if (!button) return;
        if (this.dataset.busy === 'true') return;

        this.dataset.busy = 'true';
        try {
          await postCartFetch('cart/change.js', { id: this.dataset.lineKey, quantity: 0 });
          announce('Item removed from cart');
        } catch (err) {
          console.error('[hm-cart-line-item]', err);
          delete this.dataset.busy;
        }
      }
    }
    customElements.define('hm-cart-line-item', HmCartLineItem);
  }

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-gift-wrap> — per-line gift packaging upsell.
   *
   * Three actions, three fetch patterns:
   *   - Toggle ON  → POST /cart/add.js (gift product, pack_for property)
   *   - Toggle OFF → POST /cart/change.js (qty=0 on attached gift line)
   *   - Pill swap  → sequential change.js qty=0 + add.js (mirrors the
   *                  swap pattern in snippets/hm-gift-packaging.liquid).
   *
   * For the swap, the intermediate /cart/change.js call must NOT dispatch
   * cart:change — otherwise Impact's <cart-drawer> re-fetches the section
   * mid-swap and the drawer briefly renders with no gift-wrap line, then
   * flashes again when the add completes. We use private silent helpers
   * for the swap and dispatch cart:change once at the end.
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-gift-wrap')) {
    class HmCartGiftWrap extends HTMLElement {
      #abortController;

      connectedCallback() {
        this.#abortController = new AbortController();

        const toggle = this.querySelector('[data-gift-toggle]');
        if (toggle) {
          toggle.addEventListener('change', this.#onToggle.bind(this), {
            signal: this.#abortController.signal
          });
        }

        this.addEventListener('click', this.#onPillClick.bind(this), {
          signal: this.#abortController.signal
        });

        // Keyboard navigation within the pill radiogroup. role="radio" +
        // tabindex management require us to handle Arrow/Home/End ourselves;
        // Enter/Space commits the focused pill (same as click).
        const pillsRow = this.querySelector('[role="radiogroup"]');
        if (pillsRow) {
          pillsRow.addEventListener('keydown', this.#onPillKeydown.bind(this), {
            signal: this.#abortController.signal
          });
        }
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      get #busy() {
        return this.dataset.busy === 'true';
      }

      #setBusy(value) {
        if (value) this.dataset.busy = 'true';
        else delete this.dataset.busy;
      }

      async #onToggle(event) {
        const toggle = event.target;
        if (this.#busy) {
          // Defensive: revert the visual toggle since we can't process this click
          toggle.checked = !toggle.checked;
          return;
        }

        if (toggle.checked) {
          // Toggle ON → add gift packaging with the currently-active pill,
          // falling back to the first pill if none is marked active. The old
          // logic always used the first pill, so a user who had previously
          // navigated/selected pill N (which sets aria-pressed=true), then
          // toggled off and back on, would be charged for the WRONG variant.
          const activePill = this.querySelector('[data-gift-pill][aria-pressed="true"]')
            || this.querySelector('[data-gift-pill].hm-cart-drawer__gift-wrap-pill--active')
            || this.querySelector('[data-gift-pill]');
          const variantId = activePill && activePill.dataset.variantId;
          if (!variantId) {
            toggle.checked = false;
            return;
          }
          // Optimistic highlight: paint the chosen pill as active before the
          // fetch resolves, so the user sees an immediate "first variant
          // selected" state. The server-rendered swap that arrives a moment
          // later will confirm the same active variant.
          this.#setActivePillVisual(activePill);
          await this.#add(variantId, toggle);
        } else {
          // Toggle OFF → remove the attached gift line
          const lineKey = this.dataset.attachedLineKey;
          if (!lineKey) return; // already detached, no-op
          await this.#remove(lineKey, toggle);
        }
      }

      async #onPillClick(event) {
        const pill = event.target.closest('[data-gift-pill]');
        if (!pill) return;
        if (this.#busy) return;

        const newVariantId = pill.dataset.variantId;
        if (!newVariantId) return;

        const isAttached = !!this.dataset.attachedLineKey;
        if (!isAttached) {
          // Pills row is hidden via CSS when not attached, so this path is
          // rarely hit (only via keyboard if focus escapes). Visually mark
          // the pill so when toggle flips on, the choice persists. The next
          // section refresh will reset visual state from server data.
          this.#setActivePillVisual(pill);
          return;
        }

        if (newVariantId === this.dataset.currentVariantId) return;

        await this.#swap(newVariantId, pill);
      }

      #setActivePillVisual(pill) {
        // Group of toggle buttons (single-select). aria-pressed pairs with the
        // role="group" / <button> markup; aria-checked would only be correct
        // inside a role="radiogroup", which we deliberately avoided because
        // it requires selection-follows-focus (each arrow key = cart mutation).
        const pills = this.querySelectorAll('[data-gift-pill]');
        pills.forEach((p) => {
          const active = p === pill;
          p.classList.toggle('hm-cart-drawer__gift-wrap-pill--active', active);
          p.setAttribute('aria-pressed', active ? 'true' : 'false');
          p.setAttribute('tabindex', active ? '0' : '-1');
        });
      }

      #onPillKeydown(event) {
        const NAV_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        const COMMIT_KEYS = [' ', 'Enter'];
        if (!NAV_KEYS.includes(event.key) && !COMMIT_KEYS.includes(event.key)) return;

        const pills = Array.from(this.querySelectorAll('[data-gift-pill]'));
        if (!pills.length) return;

        const currentIndex = pills.findIndex((p) => p === document.activeElement);

        if (COMMIT_KEYS.includes(event.key)) {
          if (currentIndex >= 0) {
            event.preventDefault();
            pills[currentIndex].click();
          }
          return;
        }

        event.preventDefault();
        let nextIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = pills.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = currentIndex <= 0 ? pills.length - 1 : currentIndex - 1;
        } else {
          nextIndex = currentIndex >= pills.length - 1 ? 0 : currentIndex + 1;
        }

        const next = pills[nextIndex];
        if (!next) return;
        // Move tabindex first so focus order matches the new active pill.
        pills.forEach((p) => p.setAttribute('tabindex', p === next ? '0' : '-1'));
        next.focus();
      }

      #addBody(variantId) {
        // Send both keys: the human-readable `pack_for` (parent title) so the
        // cart-line property list reads naturally for line-item.liquid's
        // "loose gift" display, and the private `_pack_for_id` (parent
        // product_id, underscore-prefixed = hidden) which the drawer uses
        // for matching. Title-based matching breaks on product rename;
        // id-based matching survives it. Both are present for backwards
        // compat with any in-flight carts added before this change.
        const properties = { pack_for: this.dataset.packFor || '' };
        if (this.dataset.packForId) {
          // Bracket form to avoid the TS narrowed-object-shape warning on
          // the underscore-prefixed (private) cart-line property.
          properties['_pack_for_id'] = this.dataset.packForId;
        }
        return {
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties
          }]
        };
      }

      async #add(variantId, toggle) {
        this.#setBusy(true);
        try {
          await postCartFetch('cart/add.js', this.#addBody(variantId));
          announce('Gift wrapping added');
        } catch (err) {
          console.error('[hm-cart-gift-wrap] add failed:', err);
          if (toggle) toggle.checked = false;
          this.#setBusy(false);
          announce('Could not add gift wrapping. Please try again.');
        }
      }

      async #remove(lineKey, toggle) {
        this.#setBusy(true);
        try {
          await postCartFetch('cart/change.js', { id: lineKey, quantity: 0 });
          announce('Gift wrapping removed');
        } catch (err) {
          console.error('[hm-cart-gift-wrap] remove failed:', err);
          if (toggle) toggle.checked = true;
          this.#setBusy(false);
          announce('Could not remove gift wrapping. Please try again.');
        }
      }

      async #swap(newVariantId, pill) {
        this.#setBusy(true);
        const oldLineKey = this.dataset.attachedLineKey;
        let removed = false;

        // Optimistic pill highlight while the swap is in flight
        this.#setActivePillVisual(pill);

        try {
          // Suppress cart:change between the two fetches — otherwise the
          // drawer re-renders mid-swap with no gift line attached.
          await postCartFetch('cart/change.js', { id: oldLineKey, quantity: 0 }, { dispatch: false });
          removed = true;
          await postCartFetch('cart/add.js', this.#addBody(newVariantId));
          announce('Gift wrapping updated');
        } catch (err) {
          console.error('[hm-cart-gift-wrap] swap failed:', err);
          if (removed) {
            // Partial failure path: the old gift line is gone but the new one
            // didn't post. The drawer DOM still shows the optimistic active
            // pill on a "gift line" that no longer exists in the cart. Force
            // a fresh /cart.js read and dispatch cart:change WITH the real
            // detail.cart so Impact's <cart-drawer> re-renders against the
            // authoritative state (which is: gift wrap removed).
            try {
              const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              const res = await fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } });
              if (res.ok) {
                const cart = await res.json();
                document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart } }));
              } else {
                // Last-resort bare dispatch — Impact may re-fetch on its own.
                document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: {} }));
              }
            } catch (refreshErr) {
              console.error('[hm-cart-gift-wrap] post-swap refresh failed:', refreshErr);
              document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: {} }));
            }
          } else {
            this.#setBusy(false);
          }
          announce('Could not update gift wrapping. Please try again.');
        }
      }
    }
    customElements.define('hm-cart-gift-wrap', HmCartGiftWrap);
  }

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-terms-gate> — terms checkbox → checkout button gate.
   *
   * The drawer's section refresh swaps DOM on every cart:change,
   * which would normally reset the checkbox to unchecked. We
   * remember the checked state at the class level so it survives
   * the swap during a session. On a fresh page load the static
   * field resets — that's intentional, fresh consent per visit.
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-terms-gate')) {
    class HmCartTermsGate extends HTMLElement {
      static accepted = false;

      #abortController;

      connectedCallback() {
        this.#abortController = new AbortController();

        const checkbox = this.querySelector('[data-terms-check]');
        const button = this.querySelector('[data-terms-button]');
        if (!checkbox || !button) return;

        // Restore previous state (within this page load).
        checkbox.checked = HmCartTermsGate.accepted;
        button.disabled = !checkbox.checked;

        checkbox.addEventListener('change', () => {
          HmCartTermsGate.accepted = checkbox.checked;
          button.disabled = !checkbox.checked;
          // SR-only announcement so keyboard users know whether the action is
          // available — disabled buttons aren't re-announced by SRs when
          // their disabled state flips.
          announce(checkbox.checked ? 'Terms accepted — checkout button is now active' : 'Terms unchecked — checkout button is disabled');
        }, { signal: this.#abortController.signal });

        // Stop link clicks from bubbling to the wrapping <label>. Without
        // this, clicking the Terms & Conditions link inside the label also
        // toggles the checkbox (HTML's implicit label-click behavior),
        // which is the textbook label/link interaction trap.
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-terms-link]');
          if (link) event.stopPropagation();
        }, { signal: this.#abortController.signal });
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }
    }
    customElements.define('hm-cart-terms-gate', HmCartTermsGate);
  }

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-recommendations> — upsell carousel.
   *
   * Uses Shopify's section-render API to load server-rendered cards:
   *   /recommendations/products?product_id=X&section_id=hm-cart-recommendations&intent=related&limit=N
   *
   * Caches by `${productId}:${limit}:${cartKey}` at module scope so
   * navigating cart contents that don't change the seed product doesn't
   * re-fetch. The `data-cart-key` attribute is a concatenation of cart
   * product IDs, set server-side, so Liquid invalidates the cache for us.
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-recommendations')) {
    const _recsCache = { key: null, html: null };

    class HmCartRecommendations extends HTMLElement {
      #abortController;

      connectedCallback() {
        this.#abortController = new AbortController();
        this.addEventListener('click', this.#onClick.bind(this), {
          signal: this.#abortController.signal
        });
        this.#fetchRecommendations();
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      #injectCards(html) {
        const container = this.querySelector('[data-recommendations-cards]');
        if (!container) return;
        const wasHidden = this.hidden;
        container.innerHTML = html;

        // If after injection the cards container is still empty (all recs
        // were filtered as already-in-cart), keep the carousel hidden.
        if (!container.children.length) {
          this.hidden = true;
          return;
        }

        this.hidden = false;

        // SR announcement when the recommendations section becomes visible
        // for the first time. Subsequent injections (e.g. cache hits across
        // cart updates) don't re-announce since they're not new info.
        if (wasHidden) {
          announce('Recommended products are available below your cart items');
        }
      }

      async #fetchRecommendations() {
        const productId = this.dataset.productId;
        const limit = parseInt(this.dataset.limit, 10) || 10;
        const cartKey = this.dataset.cartKey || '';
        const fallbackCollection = this.dataset.fallbackCollection || '';
        if (!productId || productId === '0') {
          this.hidden = true;
          return;
        }

        const cacheKey = `${productId}:${limit}:${cartKey}:${fallbackCollection}`;
        if (_recsCache.key === cacheKey && _recsCache.html != null) {
          this.#injectCards(_recsCache.html);
          return;
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        try {
          // Primary: Shopify's algorithmic recommendations (Search & Discovery app).
          const recsUrl = `${root}recommendations/products?product_id=${productId}&section_id=hm-cart-recommendations&limit=${limit}`;
          let cardsHtml = await this.#fetchAndExtractCards(recsUrl, limit);

          // Fallback: merchant-curated collection if recs returned empty.
          // Shopify's collection-render endpoint ignores ?limit, so we
          // truncate client-side after parsing.
          if (!cardsHtml && fallbackCollection) {
            const fallbackUrl = `${root}collections/${fallbackCollection}?section_id=hm-cart-recommendations`;
            cardsHtml = await this.#fetchAndExtractCards(fallbackUrl, limit);
          }

          if (!cardsHtml) {
            this.hidden = true;
            return;
          }

          _recsCache.key = cacheKey;
          _recsCache.html = cardsHtml;
          this.#injectCards(cardsHtml);
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('[hm-cart-recommendations] fetch failed:', err);
            this.hidden = true;
          }
        }
      }

      async #fetchAndExtractCards(url, limit) {
        const res = await fetch(url, { signal: this.#abortController.signal });
        if (!res.ok) return null;
        const html = await res.text();
        // Reuse the module-scoped DOMParser instead of `new DOMParser()` per
        // call — DOMParser instances are stateless and reusable.
        const doc = _domParser.parseFromString(html, 'text/html');
        const newCards = doc.querySelector('[data-recommendations-cards]');
        if (!newCards || !newCards.children.length) return null;
        // Truncate if the source returned more than expected (collection-render
        // doesn't honor limit query param).
        if (limit && newCards.children.length > limit) {
          while (newCards.children.length > limit) {
            newCards.removeChild(newCards.lastElementChild);
          }
        }
        return newCards.innerHTML;
      }

      async #onClick(event) {
        const button = event.target.closest('[data-upsell-add]');
        if (!button) return;
        if (button.dataset.busy === 'true') return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.dataset.busy = 'true';
        try {
          await postCartFetch('cart/add.js', {
            items: [{ id: parseInt(variantId, 10), quantity: 1 }]
          });
          announce('Added to cart');
          // Drawer re-renders; this element is replaced; the new instance
          // re-fetches recs (cart key changed → cache miss).
        } catch (err) {
          console.error('[hm-cart-recommendations] add failed:', err);
          delete button.dataset.busy;
        }
      }
    }
    customElements.define('hm-cart-recommendations', HmCartRecommendations);
  }

  /* ─────────────────────────────────────────────────────────────
   * <hm-cart-threshold> — 3-tier progress bar
   *
   * The Liquid snippet renders the bar in its correct state on every
   * section refresh, so most of the work is server-side. The JS here
   * is purely a polish layer: it gives the fill bar a smooth animation
   * across section re-renders by remembering the last fill % at the
   * class level (static field — survives the element being re-created
   * by Impact's <cart-drawer> replaceChildren swap).
   * ───────────────────────────────────────────────────────────── */

  if (!customElements.get('hm-cart-threshold')) {
    class HmCartThreshold extends HTMLElement {
      static lastFillPct = null;

      // No instance listeners — the smooth-fill trick uses the static
      // class field. Empty disconnectedCallback satisfies the project's
      // web-component pattern requirement.
      disconnectedCallback() {}

      connectedCallback() {
        const fill = this.querySelector('[data-threshold-fill]');
        if (!fill) return;

        const targetPct = this.dataset.fillPct;
        if (targetPct == null) {
          HmCartThreshold.lastFillPct = null;
          return;
        }

        const previous = HmCartThreshold.lastFillPct;
        HmCartThreshold.lastFillPct = targetPct;

        if (previous == null || previous === targetPct) return;

        // Skip the snap-then-animate trick for users with reduced motion
        // preference. The server has already rendered the bar at targetPct
        // via the inline style; we just leave it alone.
        if (_reducedMotion) return;

        // Snap to previous width without transition, then transition to new.
        // This re-creates the smooth animation through the section-render swap.
        const prevTransition = fill.style.transition;
        fill.style.transition = 'none';
        fill.style.width = previous + '%';
        // Force reflow so the no-transition state commits
        void fill.offsetWidth;
        fill.style.transition = prevTransition || '';
        fill.style.width = targetPct + '%';
      }
    }
    customElements.define('hm-cart-threshold', HmCartThreshold);
  }
})();
